import type { QueryKey } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { bffDiscoverScreenLoadedAtom, bffUserProfileLoadedAtom, isLoggedInAtom } from 'app/atoms';
import { http } from 'app/lib/axios';
import { markBffQueryData, useQueryLPC } from 'app/lib/query';
import { bffDiscoverQueryKeys, bffUserProfileQueryKeys, bffKeys } from 'app/network/keys';
import type { BffResult } from 'app/types/bff';
import type { FunctionalUseQueryOptions } from 'app/types/network';
import type { AxiosResponse } from 'axios';
import type { PrimitiveAtom } from 'jotai/index';
import { useAtom, useAtomValue, useSetAtom } from 'jotai/index';
import { useEffect } from 'react';

const criticalUserProfileQueryKeys = {
  get_addresses: bffUserProfileQueryKeys.get_addresses,
  get_chat_unread: bffUserProfileQueryKeys.get_chat_unread,
  get_groups: bffUserProfileQueryKeys.get_groups,
  get_num_unseen_notifications: bffUserProfileQueryKeys.get_num_unseen_notifications,
  get_plans: bffUserProfileQueryKeys.get_plans,
  get_preferences: bffUserProfileQueryKeys.get_preferences,
  get_profiles: bffUserProfileQueryKeys.get_profiles,
  get_users: bffUserProfileQueryKeys.get_users,
};

export function useDiscoverScreen(opts: FunctionalUseQueryOptions<BffResult[]> = {}) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  return useGetBff('/bff/discover/', undefined, bffDiscoverQueryKeys, bffDiscoverScreenLoadedAtom, {
    ...opts,
    enabled: (opts.enabled ?? true) && isLoggedIn,
  });
}

export function useUserProfile(opts: FunctionalUseQueryOptions<BffResult[]> = {}) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);

  return useGetBff('/bff/user_profile/', undefined, criticalUserProfileQueryKeys, bffUserProfileLoadedAtom, {
    ...opts,
    enabled: (opts.enabled ?? true) && isLoggedIn,
  });
}

export function useRefetchBff() {
  const setDiscoverScreenLoaded = useSetAtom(bffDiscoverScreenLoadedAtom);
  const setBffUserProfileLoaded = useSetAtom(bffUserProfileLoadedAtom);

  return () => {
    setDiscoverScreenLoaded(false);
    setBffUserProfileLoaded(false);
  };
}

export function useGetBff(
  url: string,
  params: Record<string, unknown> | undefined,
  queryKeys: Partial<Record<BffResult['queryKey'], (queryParams: BffResult['queryParams']) => QueryKey>>,
  atom: PrimitiveAtom<boolean>,
  opts: FunctionalUseQueryOptions<BffResult[]> = {},
) {
  const [dataLoaded, setDataLoaded] = useAtom(atom);
  const client = useQueryClient();

  const query = useQueryLPC<BffResult[]>({
    queryKey: bffKeys.one(url),
    queryFn: async () => {
      const res: AxiosResponse<BffResult[], unknown> = await http.get<BffResult[]>(url, {
        params: { ...params, queryKeys: Object.keys(queryKeys) },
      });

      const updatedAt = Date.now();
      res.data.forEach((bffResult) => {
        const queryKey = queryKeys[bffResult.queryKey]?.(bffResult.queryParams);
        if (queryKey) {
          if (bffResult.data === undefined) {
            return;
          }

          if (bffResult.isPaginated) {
            client.setQueryData(queryKey, { pages: [bffResult.data], pageParams: [null] }, { updatedAt });
          } else {
            client.setQueryData(queryKey, bffResult.data, { updatedAt });
          }
          markBffQueryData(queryKey, updatedAt);
        }
      });
      setDataLoaded(true);

      return res.data;
    },
    ...opts,
    enabled: (opts.enabled ?? true) && !dataLoaded,
  });

  useEffect(() => {
    // After the configured retries are exhausted, release dependent hooks
    // so visible screens can use their targeted endpoint fallbacks.
    if (query.isError) setDataLoaded(true);
  }, [query.isError, setDataLoaded]);

  return query;
}
